import { useEffect, useState } from 'react';
import styles from './Game.module.scss';
import classNames from 'classnames';
import { OPERATOR, DRIVER, LAB, FILLER, stories } from '@/src/constants';
import { createText } from '@/src/utils/createText';
import { getArray } from '@/src/utils/getArray';
import { Footer } from '@/src/components/Footer';
import { CSSTransition } from 'react-transition-group';
import sendEventToCounters from '@/src/counterEvents';

type TProps = {
  changePage: (pageName: string) => void;
  activeStory: string;
};

export const Game = ({ changePage, activeStory }: TProps) => {
  useEffect(() => {
    setTimeout(() => {
      window.scrollTo(0, 0);
    });
  }, []);

  const [question, setQuestion] = useState(1);
  const [answer, setAnswer] = useState(0);

  const [isAnimated, setIsAnimated] = useState(false);
  const [isAnimatedQuestion, setIsAnimatedQuestion] = useState(false);

  let timerId: ReturnType<typeof setTimeout>;

  useEffect(() => {
    timerId = setTimeout(() => {
      setIsAnimated(true);
      setIsAnimatedQuestion(true);
    }, 1);

    return () => clearTimeout(timerId);
  }, []);

  const handleSetAnswer = (number: number) => {
    setAnswer(number);
    setTimeout(() => {
      window.scrollTo(0, document.body.scrollHeight);
    }, 1);
  };

  const handleGoNext = () => {
    if (question === Object.keys(stories[activeStory].questions).length) {
      changePage('final');
    } else {
      setIsAnimated(true);
      if (document.documentElement.clientWidth > 1020) {
        setIsAnimatedQuestion(false);
        setTimeout(() => {
          setIsAnimatedQuestion(true);
          setAnswer(0);
          setQuestion(question + 1);
          window.scrollTo(0, 0);
        }, 200);
      } else {
        setIsAnimatedQuestion(true);
        setTimeout(() => {
          window.scrollTo(0, 0);
        }, 1);
        setAnswer(0);
        setQuestion(question + 1);
      }
    }
    switch (activeStory) {
      case OPERATOR:
        sendAnalytics('kassir_azs');
        break;
      case DRIVER:
        sendAnalytics('voditel');
        break;
      case LAB:
        sendAnalytics('laborant');
        break;
      case FILLER:
        sendAnalytics('napolnitel');
        break;
      default:
        break;
    }
  };

  const sendAnalytics = (action: string) => {
    sendEventToCounters({ action, label: `question_${question}` });
  };

  return (
    <div className={classNames(styles.game, 'game')}>
      <div className={styles.top}>
        <div className={classNames('container', styles.container)}>
          <div className={classNames(styles.topWrapper, styles[activeStory])}>
            <div className={styles.image}>
              <img src={stories[activeStory].image} alt="" />
            </div>
            <div className={styles.wrap}>
              <div className={styles.gameWrap}>
                <CSSTransition
                  in={isAnimated}
                  timeout={200}
                  classNames="fadeLeft"
                >
                  <div
                    className={classNames(
                      styles.wrapTop,
                      'fadeLeft',
                      'fadeTitle',
                    )}
                  >
                    <div
                      className={classNames(styles.title)}
                      dangerouslySetInnerHTML={createText(
                        stories[activeStory].title,
                      )}
                    ></div>
                  </div>
                </CSSTransition>
                <CSSTransition
                  in={isAnimatedQuestion}
                  timeout={200}
                  classNames={
                    activeStory === LAB ? 'fadeGameRight' : 'fadeGameLeft'
                  }
                >
                  <div
                    className={classNames(
                      styles.wrapContent,
                      activeStory === LAB ? 'fadeGameRight' : 'fadeGameLeft',
                      'fadeContent',
                    )}
                  >
                    {stories[activeStory].questions[question].extra && (
                      <div
                        className={classNames(styles.extra, styles.extraTop)}
                      >
                        <p
                          dangerouslySetInnerHTML={createText(
                            stories[activeStory].questions[question].extra,
                          )}
                        ></p>
                      </div>
                    )}
                    <div
                      className={classNames(
                        styles.content,
                        {
                          [styles.contentWithoutTriangle]:
                            stories[activeStory].questions[question].extra,
                        },
                        {
                          [styles.contentWithBottomExtra]:
                            answer !== 0 &&
                            stories[activeStory].questions[question].answers[
                              answer - 1
                            ].extra,
                        },
                      )}
                    >
                      <div
                        className={styles.question}
                        dangerouslySetInnerHTML={createText(
                          stories[activeStory].questions[question].question,
                        )}
                      ></div>
                      <div className={styles.contentWrap}>
                        <div className={styles.buttons}>
                          {stories[activeStory].questions[question].answers.map(
                            (
                              item: { answer: string; comment: string },
                              index: number,
                            ) => (
                              <button
                                key={index}
                                className={classNames(
                                  styles.button,
                                  {
                                    [styles.buttonActive]: answer === index + 1,
                                  },
                                  {
                                    [styles.buttonSmall]:
                                      (activeStory === OPERATOR &&
                                        question === 4) ||
                                      (activeStory === LAB && question === 4) ||
                                      (activeStory === FILLER &&
                                        question === 2),
                                  },
                                )}
                                dangerouslySetInnerHTML={createText(
                                  item.answer,
                                )}
                                onClick={() => handleSetAnswer(index + 1)}
                              ></button>
                            ),
                          )}
                        </div>
                        {answer !== 0 && (
                          <div
                            className={styles.comment}
                            dangerouslySetInnerHTML={createText(
                              stories[activeStory].questions[question].answers[
                                answer - 1
                              ].comment,
                            )}
                          ></div>
                        )}
                      </div>
                    </div>
                    {answer !== 0 &&
                      stories[activeStory].questions[question].answers[
                        answer - 1
                      ].extra && (
                        <div
                          className={classNames(
                            styles.extra,
                            styles.extraBottom,
                          )}
                        >
                          <p
                            dangerouslySetInnerHTML={createText(
                              stories[activeStory].questions[question].answers[
                                answer - 1
                              ].extra,
                            )}
                          ></p>
                        </div>
                      )}
                  </div>
                </CSSTransition>
              </div>
              {answer !== 0 && (
                <div className={styles.next}>
                  <div className={styles.nextQuestion}>
                    <p
                      dangerouslySetInnerHTML={createText(
                        question ===
                          Object.keys(stories[activeStory].questions).length
                          ? stories[activeStory].finalText
                          : stories[activeStory].questions[question + 1]
                              .extra ||
                              stories[activeStory].questions[question + 1]
                                .question,
                      )}
                    ></p>
                  </div>
                  <button
                    className={classNames('button', styles.nextButton)}
                    onClick={handleGoNext}
                  >
                    Следующий вопрос
                  </button>
                </div>
              )}
              <div className={styles.pagination}>
                {getArray(
                  Object.keys(stories[activeStory].questions).length,
                ).map((bullet) => (
                  <div
                    key={bullet}
                    className={classNames(
                      styles.bullet,
                      {
                        [styles.bulletActive]: question === bullet,
                      },
                      {
                        [styles.bulletLast]: question > bullet,
                      },
                    )}
                  >
                    {bullet}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className={styles.bottom}>
        <Footer />
      </div>
    </div>
  );
};
